import { ReactNode, useEffect, useMemo } from 'react';
import { Link } from 'react-router-dom';
import { useForm, Controller } from 'react-hook-form';
import { toast } from 'sonner';
import {
  ArrowLeft,
  User,
  Bell,
  Lock,
  Eye,
  Shield,
  Heart,
  Loader2,
} from 'lucide-react';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Label } from '../ui/label';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../ui/card';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../ui/tabs';
import { Switch } from '../ui/switch';
import { Textarea } from '../ui/textarea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../ui/select';
import { Avatar, AvatarFallback } from '../ui/avatar';
import { Separator } from '../ui/separator';
import { Checkbox } from '../ui/checkbox';
import { useCandidateProfileSection, useUpdateCandidateProfileSection } from '../../hooks/useCandidateProfile';
import { useCandidatePrivacySettings, useUpdateCandidatePrivacySettings } from '../../hooks/useCandidateSettings';
import { useAuth } from '../../hooks/useAuth';
import type { CandidateProfileSectionEntry } from '../../api/candidate';

const notificationChannelOptions = ['email', 'sms', 'in-app'] as const;
type NotificationChannel = (typeof notificationChannelOptions)[number];

type PersonalInfoFormValues = {
  fullName: string;
  headline: string;
  email: string;
  location: string;
  summary: string;
  availability: string;
  desiredRoles: string;
  experienceLevel: 'early' | 'mid' | 'senior';
  workStyle: 'remote' | 'hybrid' | 'onsite';
  salaryRange: string;
  jobAlerts: boolean;
  alertFrequency: 'daily' | 'weekly' | 'off';
  notificationChannels: NotificationChannel[];
  returner: boolean;
};

const DEFAULT_VALUES: PersonalInfoFormValues = {
  fullName: '',
  headline: '',
  email: '',
  location: '',
  summary: '',
  availability: '',
  desiredRoles: '',
  experienceLevel: 'mid',
  workStyle: 'hybrid',
  salaryRange: '',
  jobAlerts: true,
  alertFrequency: 'weekly',
  notificationChannels: ['email'],
  returner: true,
};

export function CandidateSettings() {
  const personalInfoQuery = useCandidateProfileSection('personal-info');
  const updatePersonalInfo = useUpdateCandidateProfileSection('personal-info');
  const form = useForm<PersonalInfoFormValues>({ defaultValues: DEFAULT_VALUES });
  const { user } = useAuth();
  const authEmail = user?.email?.toLowerCase();
  const privacySettingsQuery = useCandidatePrivacySettings();
  const updatePrivacySettings = useUpdateCandidatePrivacySettings();

  const personalInfoEntry = (personalInfoQuery.data?.[0] ??
    undefined) as CandidateProfileSectionEntry | undefined;

  useEffect(() => {
    if (personalInfoQuery.isPending) return;
    form.reset(mapEntryToValues(personalInfoEntry, authEmail));
  }, [personalInfoEntry, personalInfoQuery.isPending, form, authEmail]);

  const watchFullName = form.watch('fullName');
  const watchJobAlerts = form.watch('jobAlerts');

  const initials = useMemo(() => getInitials(watchFullName), [watchFullName]);
  const isSaving = updatePersonalInfo.isPending;
  const profileVisible = privacySettingsQuery.data?.profileVisible ?? true;
  const privacyLoading = privacySettingsQuery.isPending || updatePrivacySettings.isPending;

  const handleVisibilityChange = async (value: boolean) => {
    try {
      await updatePrivacySettings.mutateAsync({ profileVisible: value });
      toast.success(value ? 'Profile visible to verified employers' : 'Profile hidden from employers');
    } catch (error) {
      const message = error instanceof Error ? error.message : 'Unable to update privacy settings';
      toast.error(message);
    }
  };

  const handleSubmit = form.handleSubmit(async (values) => {
    try {
      await updatePersonalInfo.mutateAsync([mapValuesToEntry(values)]);
      toast.success('Preferences saved');
      form.reset(values);
    } catch (err) {
      const message = err instanceof Error ? err.message : 'Unable to save changes';
      toast.error(message);
    }
  });

  return (
    <div className="relative min-h-screen overflow-hidden bg-[radial-gradient(circle_at_6%_10%,rgba(99,102,241,0.18),transparent_52%),linear-gradient(180deg,#030712_0%,#020816_70%,#01020a_100%)]">
      <header className="sticky top-0 z-40 border-b border-white/10 bg-[rgba(5,9,19,0.85)] backdrop-blur-md">
        <div className="mx-auto flex max-w-7xl items-center justify-between px-4 py-4 sm:px-6 lg:px-8">
          <Link
            to="/candidate/dashboard"
            className="inline-flex items-center gap-2 text-gray-400 transition-colors hover:text-white"
          >
            <ArrowLeft className="h-4 w-4" />
            Back to Dashboard
          </Link>
          <Button
            variant="ghost"
            className="text-sm text-gray-300 hover:text-white"
            asChild
          >
            <Link to="/candidate/profile-strength">View profile strength</Link>
          </Button>
        </div>
      </header>

      <div className="mx-auto max-w-5xl px-4 py-8 sm:px-6 lg:px-8">
        <div className="mb-8">
          <h1 className="mb-2 text-gold-gradient">Settings</h1>
          <p className="text-gray-400">Manage your profile, visibility, and notification preferences.</p>
        </div>

        {personalInfoQuery.isError && (
          <div className="mb-6 rounded-xl border border-red-500/40 bg-red-500/10 px-4 py-3 text-sm text-red-200">
            {personalInfoQuery.error instanceof Error
              ? personalInfoQuery.error.message
              : 'Unable to load your details. Please refresh and try again.'}
          </div>
        )}

        <form onSubmit={handleSubmit} className="space-y-6">
          <Tabs defaultValue="profile" className="space-y-6">
            <TabsList className="border border-white/10 bg-white/5">
              <TabsTrigger
                value="profile"
                className="data-[state=active]:bg-gold-gradient data-[state=active]:text-black"
              >
                <User className="mr-2 h-4 w-4" />
                Profile
              </TabsTrigger>
              <TabsTrigger
                value="notifications"
                className="data-[state=active]:bg-gold-gradient data-[state=active]:text-black"
              >
                <Bell className="mr-2 h-4 w-4" />
                Notifications
              </TabsTrigger>
              <TabsTrigger
                value="privacy"
                className="data-[state=active]:bg-gold-gradient data-[state=active]:text-black"
              >
                <Eye className="mr-2 h-4 w-4" />
                Privacy
              </TabsTrigger>
              <TabsTrigger
                value="security"
                className="data-[state=active]:bg-gold-gradient data-[state=active]:text-black"
              >
                <Lock className="mr-2 h-4 w-4" />
                Security
              </TabsTrigger>
            </TabsList>

            <TabsContent value="profile" className="space-y-6">
              <Card className="border-white/10 bg-white/5">
                <CardHeader>
                  <CardTitle className="text-white">Personal information</CardTitle>
                  <CardDescription className="text-gray-400">
                    The details you edit here power Matches, Profile Strength, and the employer view of your
                    profile.
                  </CardDescription>
                </CardHeader>
                <CardContent className="space-y-6">
                  <div className="flex flex-wrap items-center gap-6">
                    <Avatar className="h-20 w-20 text-lg">
                      <AvatarFallback className="bg-gradient-to-br from-[#ae8625] to-[#f7ef8a] text-black">
                        {initials}
                      </AvatarFallback>
                    </Avatar>
                    <div>
                      <p className="text-sm text-gray-400">
                        Your photo support is coming soon. For now we show your initials.
                      </p>
                    </div>
                  </div>

                  <Separator className="bg-white/10" />

                  <div className="grid gap-4 md:grid-cols-2">
                    <Field label="Full name">
                      <Input
                        {...form.register('fullName')}
                        disabled={isSaving || personalInfoQuery.isPending}
                        className="border-white/20 bg-white/10 text-white"
                        placeholder="e.g. Sarah Johnson"
                      />
                    </Field>
                    <Field label="Headline">
                      <Input
                        {...form.register('headline')}
                        disabled={isSaving || personalInfoQuery.isPending}
                        className="border-white/20 bg-white/10 text-white"
                        placeholder="Experienced care lead championing returners"
                      />
                    </Field>
                    <Field label="Email address">
                      <Input
                        type="email"
                        {...form.register('email')}
                        readOnly
                        className="border-white/20 bg-white/10 text-white"
                        placeholder="you@example.com"
                      />
                      <p className="text-xs text-gray-400">Managed via Auth0. Contact support if you need to change it.</p>
                    </Field>
                    <Field label="Location">
                      <Input
                        {...form.register('location')}
                        disabled={isSaving || personalInfoQuery.isPending}
                        className="border-white/20 bg-white/10 text-white"
                        placeholder="City, Country"
                      />
                    </Field>
                    <Field label="Availability">
                      <Input
                        {...form.register('availability')}
                        disabled={isSaving || personalInfoQuery.isPending}
                        className="border-white/20 bg-white/10 text-white"
                        placeholder="Immediately / May 2025 / etc"
                      />
                    </Field>
                    <Field label="Desired roles">
                      <Input
                        {...form.register('desiredRoles')}
                        disabled={isSaving || personalInfoQuery.isPending}
                        className="border-white/20 bg-white/10 text-white"
                        placeholder="Care Lead, Service Manager"
                      />
                    </Field>
                    <div>
                      <Label className="text-white">Experience level</Label>
                      <Controller
                        control={form.control}
                        name="experienceLevel"
                        render={({ field }) => (
                          <Select
                            value={field.value}
                            onValueChange={field.onChange}
                            disabled={isSaving || personalInfoQuery.isPending}
                          >
                            <SelectTrigger className="border-white/20 bg-white/10 text-white">
                              <SelectValue placeholder="Select level" />
                            </SelectTrigger>
                            <SelectContent className="border-white/10 bg-[#0d1022] text-white">
                              <SelectItem value="early">Early career</SelectItem>
                              <SelectItem value="mid">Mid level</SelectItem>
                              <SelectItem value="senior">Senior / Lead</SelectItem>
                            </SelectContent>
                          </Select>
                        )}
                      />
                    </div>
                    <div>
                      <Label className="text-white">Work style preference</Label>
                      <Controller
                        control={form.control}
                        name="workStyle"
                        render={({ field }) => (
                          <Select
                            value={field.value}
                            onValueChange={field.onChange}
                            disabled={isSaving || personalInfoQuery.isPending}
                          >
                            <SelectTrigger className="border-white/20 bg-white/10 text-white">
                              <SelectValue placeholder="Select work style" />
                            </SelectTrigger>
                            <SelectContent className="border-white/10 bg-[#0d1022] text-white">
                              <SelectItem value="remote">Remote</SelectItem>
                              <SelectItem value="hybrid">Hybrid</SelectItem>
                              <SelectItem value="onsite">On-site</SelectItem>
                            </SelectContent>
                          </Select>
                        )}
                      />
                    </div>
                    <Field label="Salary expectations">
                      <Input
                        {...form.register('salaryRange')}
                        disabled={isSaving || personalInfoQuery.isPending}
                        className="border-white/20 bg-white/10 text-white"
                        placeholder="£55k - £65k"
                      />
                    </Field>
                  </div>

                  <div>
                    <Label className="text-white">Professional summary</Label>
                    <Textarea
                      {...form.register('summary')}
                      disabled={isSaving || personalInfoQuery.isPending}
                      rows={5}
                      className="border-white/20 bg-white/10 text-white"
                      placeholder="Tell employers about your impact, leadership style, and what you’re targeting next."
                    />
                  </div>

                  <div className="flex items-center justify-between rounded-xl border border-white/10 bg-white/[0.08] px-4 py-3">
                    <div>
                      <p className="font-medium text-white">Returner programme</p>
                      <p className="text-sm text-gray-300">
                        Highlight that you’re part of Lighthouse’s returner community.
                      </p>
                    </div>
                    <Controller
                      control={form.control}
                      name="returner"
                      render={({ field }) => (
                        <Switch
                          checked={field.value}
                          onCheckedChange={field.onChange}
                          disabled={isSaving || personalInfoQuery.isPending}
                        />
                      )}
                    />
                  </div>

                  <div className="flex flex-wrap gap-3">
                    <Button
                      type="submit"
                      className="bg-gold-gradient text-black hover:opacity-90"
                      disabled={isSaving || !form.formState.isDirty}
                    >
                      {isSaving && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}
                      Save changes
                    </Button>
                    <Button
                      type="button"
                      variant="ghost"
                      onClick={() => form.reset(mapEntryToValues(personalInfoEntry))}
                      disabled={isSaving || personalInfoQuery.isPending || !form.formState.isDirty}
                    >
                      Reset
                    </Button>
                  </div>
                </CardContent>
              </Card>
            </TabsContent>

            <TabsContent value="notifications">
              <Card className="border-white/10 bg-white/5">
                <CardHeader>
                  <CardTitle className="text-white">Job alerts & updates</CardTitle>
                  <CardDescription className="text-gray-400">
                    Control how Lighthouse keeps you in the loop.
                  </CardDescription>
                </CardHeader>
                <CardContent className="space-y-6">
                  <div className="flex items-center justify-between rounded-xl border border-white/10 bg-white/[0.08] px-4 py-3">
                    <div>
                      <p className="font-medium text-white">Job alerts</p>
                      <p className="text-sm text-gray-300">
                        Receive curated roles that match your profile strength.
                      </p>
                    </div>
                    <Controller
                      control={form.control}
                      name="jobAlerts"
                      render={({ field }) => (
                        <Switch
                          checked={field.value}
                          onCheckedChange={field.onChange}
                          disabled={isSaving}
                        />
                      )}
                    />
                  </div>

                  <div className="grid gap-4 md:grid-cols-2">
                    <div>
                      <Label className="text-white">Alert frequency</Label>
                      <Controller
                        control={form.control}
                        name="alertFrequency"
                        render={({ field }) => (
                          <Select
                            value={field.value}
                            onValueChange={field.onChange}
                            disabled={!watchJobAlerts || isSaving}
                          >
                            <SelectTrigger className="border-white/20 bg-white/10 text-white">
                              <SelectValue placeholder="Choose cadence" />
                            </SelectTrigger>
                            <SelectContent className="border-white/10 bg-[#0d1022] text-white">
                              <SelectItem value="daily">Daily highlights</SelectItem>
                              <SelectItem value="weekly">Weekly digest</SelectItem>
                              <SelectItem value="off">Pause alerts</SelectItem>
                            </SelectContent>
                          </Select>
                        )}
                      />
                    </div>
                    <div>
                      <Label className="text-white">Channels</Label>
                      <Controller
                        control={form.control}
                        name="notificationChannels"
                        render={({ field }) => (
                          <div className="space-y-2 rounded-xl border border-white/10 bg-white/5 p-4 text-sm text-white">
                            {notificationChannelOptions.map((channel) => (
                              <label key={channel} className="flex items-center gap-3 capitalize">
                                <Checkbox
                                  checked={field.value?.includes(channel)}
                                  onCheckedChange={(checked) => {
                                    const next = checked
                                      ? [...(field.value ?? []), channel]
                                      : (field.value ?? []).filter((item) => item !== channel);
                                    field.onChange(next.length ? next : []);
                                  }}
                                  disabled={!watchJobAlerts || isSaving}
                                />
                                {channel === 'in-app' ? 'In-app' : channel.toUpperCase()}
                              </label>
                            ))}
                          </div>
                        )}
                      />
                    </div>
                  </div>

                  <p className="text-sm text-gray-400">
                    We also log delivery failures inside Lighthouse Monitor so you never miss a message.
                  </p>

                  <div className="flex flex-wrap gap-3">
                    <Button
                      type="submit"
                      className="bg-gold-gradient text-black hover:opacity-90"
                      disabled={isSaving || !form.formState.isDirty}
                    >
                      {isSaving && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}
                      Save preferences
                    </Button>
                    <Button
                      type="button"
                      variant="ghost"
                      onClick={() => form.reset(mapEntryToValues(personalInfoEntry))}
                      disabled={isSaving || personalInfoQuery.isPending || !form.formState.isDirty}
                    >
                      Reset
                    </Button>
                  </div>
                </CardContent>
              </Card>
            </TabsContent>

            <TabsContent value="privacy">
              <Card className="border-white/10 bg-white/5">
                <CardHeader>
                  <CardTitle className="text-white">Visibility controls</CardTitle>
                  <CardDescription className="text-gray-400">
                    Fine-tune when employers can discover you. These controls will sync with DPA tooling soon.
                  </CardDescription>
                </CardHeader>
                <CardContent className="space-y-4">
                  <div className="flex items-center justify-between rounded-xl border border-white/10 bg-white/[0.08] px-4 py-3">
                    <div>
                      <p className="font-medium text-white">Show profile to verified employers</p>
                      <p className="text-sm text-gray-300">
                        When disabled, only Lighthouse staff can view your profile.
                      </p>
                    </div>
                    <Switch
                      checked={profileVisible}
                      disabled={privacyLoading}
                      onCheckedChange={handleVisibilityChange}
                    />
                  </div>
                  <div className="rounded-xl border border-white/10 bg-white/[0.08] px-4 py-3 text-sm text-gray-300">
                    <p>
                      Additional controls (PII scrubbing, per-company visibility, and GDPR exports/deletes) are handled
                      by the moderation team. Use the{" "}
                      <Link to="/contact" className="text-gold underline">
                        support form
                      </Link>{" "}
                      to request an export or deletion of your data.
                    </p>
                  </div>
                </CardContent>
              </Card>
            </TabsContent>

            <TabsContent value="security">
              <Card className="border-white/10 bg-white/5">
                <CardHeader>
                  <CardTitle className="text-white">Account security</CardTitle>
                  <CardDescription className="text-gray-400">
                    Auth0 enforces MFA and anomaly detection. Need extra safeguards? Let us know.
                  </CardDescription>
                </CardHeader>
                <CardContent className="grid gap-4 md:grid-cols-2">
                  <SecurityTile
                    icon={Shield}
                    title="Multi-factor auth"
                    description="Protected by Auth0 Guardian. Enforcement is handled centrally for all admin/candidate logins."
                  />
                  <SecurityTile
                    icon={Heart}
                    title="Session monitoring"
                    description="Lighthouse Monitor keeps an eye on login anomalies so we can revoke risky sessions fast."
                  />
                </CardContent>
              </Card>
            </TabsContent>
          </Tabs>
        </form>
      </div>
    </div>
  );
}

function Field({ label, children }: { label: string; children: ReactNode }) {
  return (
    <div className="space-y-2">
      <Label className="text-white">{label}</Label>
      {children}
    </div>
  );
}

function SecurityTile({
  icon: Icon,
  title,
  description,
}: {
  icon: typeof Shield;
  title: string;
  description: string;
}) {
  return (
    <div className="space-y-2 rounded-xl border border-white/10 bg-white/[0.08] p-5 text-sm text-gray-300">
      <Icon className="h-5 w-5 text-gold" />
      <p className="font-semibold text-white">{title}</p>
      <p>{description}</p>
    </div>
  );
}

function getInitials(fullName: string) {
  if (!fullName?.trim()) return 'YOU';
  const parts = fullName.trim().split(/\s+/);
  const letters = `${parts[0]?.[0] ?? ''}${parts[parts.length - 1]?.[0] ?? ''}`;
  return letters.toUpperCase();
}

function mapEntryToValues(
  entry?: CandidateProfileSectionEntry | null,
  fallbackEmail?: string,
): PersonalInfoFormValues {
  const base: PersonalInfoFormValues = { ...DEFAULT_VALUES };
  if (!entry) {
    return {
      ...base,
      email: fallbackEmail ?? base.email,
    };
  }
  return {
    ...base,
    fullName: entry.fullName ?? DEFAULT_VALUES.fullName,
    headline: entry.headline ?? DEFAULT_VALUES.headline,
    email: entry.email ?? fallbackEmail ?? DEFAULT_VALUES.email,
    location: entry.location ?? DEFAULT_VALUES.location,
    summary: entry.summary ?? DEFAULT_VALUES.summary,
    availability: entry.availability ?? DEFAULT_VALUES.availability,
    desiredRoles: entry.desiredRoles ?? DEFAULT_VALUES.desiredRoles,
    experienceLevel: isExperienceLevel(entry.experienceLevel) ? entry.experienceLevel : DEFAULT_VALUES.experienceLevel,
    workStyle: isWorkStyle(entry.workStyle) ? entry.workStyle : DEFAULT_VALUES.workStyle,
    salaryRange: entry.salaryRange ?? DEFAULT_VALUES.salaryRange,
    jobAlerts: entry.jobAlerts ? entry.jobAlerts !== 'false' : DEFAULT_VALUES.jobAlerts,
    alertFrequency: isFrequency(entry.alertFrequency) ? entry.alertFrequency : DEFAULT_VALUES.alertFrequency,
    notificationChannels: parseChannels(entry.notificationChannels) ?? DEFAULT_VALUES.notificationChannels,
    returner: entry.returner ? entry.returner !== 'false' : DEFAULT_VALUES.returner,
  };
}

function mapValuesToEntry(values: PersonalInfoFormValues): CandidateProfileSectionEntry {
  return {
    fullName: values.fullName.trim(),
    headline: values.headline.trim(),
    email: values.email.trim(),
    location: values.location.trim(),
    summary: values.summary.trim(),
    availability: values.availability.trim(),
    desiredRoles: values.desiredRoles.trim(),
    experienceLevel: values.experienceLevel,
    workStyle: values.workStyle,
    salaryRange: values.salaryRange.trim(),
    jobAlerts: values.jobAlerts ? 'true' : 'false',
    alertFrequency: values.alertFrequency,
    notificationChannels: values.notificationChannels.join(', '),
    returner: values.returner ? 'true' : 'false',
  };
}

function isWorkStyle(value?: string): value is PersonalInfoFormValues['workStyle'] {
  return value === 'remote' || value === 'hybrid' || value === 'onsite';
}

function isFrequency(value?: string): value is PersonalInfoFormValues['alertFrequency'] {
  return value === 'daily' || value === 'weekly' || value === 'off';
}

function isExperienceLevel(value?: string): value is PersonalInfoFormValues['experienceLevel'] {
  return value === 'early' || value === 'mid' || value === 'senior';
}

function parseChannels(value?: string): NotificationChannel[] | undefined {
  if (!value) return undefined;
  const parsed = value
    .split(',')
    .map((item) => item.trim())
    .filter((channel): channel is NotificationChannel => isChannel(channel));
  return parsed.length ? parsed : undefined;
}

function isChannel(value: string): value is NotificationChannel {
  return (notificationChannelOptions as readonly string[]).includes(value);
}
